import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { TDesignSlide } from "@/shared/types";
import type { CreateAiSlideState } from "./types";

const initialState: CreateAiSlideState = {
  prompt: "",
  status: "idle",
  design: null,
  designId: null,
  contentSlide: {
    id: "",
    placement: "bottom",
  },
  regenerateStory: false,
  buildingSlideId: null,
  referenceImageUrl: null,
  referenceText: null,
};

export const aiSlidesSlice = createSlice({
  name: "ai-slides",
  initialState,
  reducers: {
    setPrompt: (state: CreateAiSlideState, action: PayloadAction<string>) => {
      state.prompt = action.payload;
    },
    setDesign: (
      state: CreateAiSlideState,
      action: PayloadAction<TDesignSlide>,
    ) => {
      state.design = action.payload;
      state.designId = null;
    },
    setDesignId: (state: CreateAiSlideState, action: PayloadAction<string>) => {
      state.designId = action.payload;
      state.design = null;
    },
    setStatus: (
      state: CreateAiSlideState,
      action: PayloadAction<
        "idle" | "loading" | "error" | "success" | "cancel"
      >,
    ) => {
      state.status = action.payload;
    },
    /** The staged wireframe's root id, or null once it is done. */
    setBuildingSlideId: (
      state: CreateAiSlideState,
      action: PayloadAction<string | null>,
    ) => {
      state.buildingSlideId = action.payload;
    },
    setContentSlide: (
      state: CreateAiSlideState,
      action: PayloadAction<{ id: string; placement: "bottom" | "replace" }>,
    ) => {
      state.contentSlide = action.payload;
    },
    setMany: (
      state: CreateAiSlideState,
      action: PayloadAction<Partial<CreateAiSlideState>>,
    ) => {
      const {
        status,
        design,
        designId,
        contentSlide,
        referenceImageUrl,
        referenceText,
      } = action.payload;
      if (status) state.status = status;
      if (contentSlide) state.contentSlide = contentSlide;
      // `!== undefined`, NOT truthiness: `design` and `designId` are the two
      // ways of naming what to build, and setting one has to be able to CLEAR
      // the other. A caller passing `designId: null` alongside its own design
      // was silently ignored, so a designId left behind by an earlier
      // regenerate still won -- getDesignData prefers it and goes off to fetch
      // a design the user never picked.
      if (design !== undefined) state.design = design;
      if (designId !== undefined) state.designId = designId;
      if (referenceImageUrl !== undefined)
        state.referenceImageUrl = referenceImageUrl;
      if (referenceText !== undefined) state.referenceText = referenceText;
    },
    setRegenerateStory: (
      state: CreateAiSlideState,
      action: PayloadAction<boolean>,
    ) => {
      state.regenerateStory = action.payload;
    },
    reset: () => initialState,
  },
});

export const {
  setPrompt,
  setDesign,
  setDesignId,
  setStatus,
  setContentSlide,
  setBuildingSlideId,
  setMany,
  reset,
  setRegenerateStory,
} = aiSlidesSlice.actions;
